import { Component, ElementRef, HostBinding, inject, Input, OnDestroy, OnInit, Optional, QueryList, Self, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule, NgControl, NgModel, ReactiveFormsModule } from '@angular/forms';
import { ControlBaseComponent } from '../control-base/control-base.component';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MinsterDate } from '../../../models/minster-date.model';
import { StateService } from '../../../services/state.service';
import { ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import moment from 'moment';

@Component({
    selector: 'app-minster-dob',
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule],
    templateUrl: './minster-dob.component.html',
    styleUrl: './minster-dob.component.css',
    providers: [{ provide: MatFormFieldControl, useExisting: MinsterDobComponent }],
})

export class MinsterDobComponent extends ControlBaseComponent<MinsterDate> implements MatFormFieldControl<MinsterDate>, OnDestroy, OnInit {
    stateChanges = new Subject<void>();
    id: string;
    focused = false;
    private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    @ViewChild('one') _one!: ElementRef;
    @ViewChild('two') _two!: ElementRef;
    @ViewChild('three') _three!: ElementRef;
    @ViewChildren(NgModel) _inputs!: QueryList<NgModel>;

    @Input()
    get value(): MinsterDate | null {
        return this._value;
    }
    set value(val: MinsterDate | null) {
        if (val === null) {
            this._value = new MinsterDate(null, null, null);
        } else {
            this._value = val;
        }
        this.stateChanges.next();
    }

    @Input()
    get placeholder() {
        return this._placeHolder;
    }
    set placeholder(plh) {
        this._placeHolder = plh;
        this.stateChanges.next();
    }

    constructor(@Optional() @Self() public ngControl: NgControl, stateService: StateService) {
        super(ngControl, stateService);
        this.id = this._name;
        this._value = new MinsterDate(null, null, null);
    }

    ngOnInit(): void {
        this.disabled = this._disabled;
        this.required = this._required;
        this.id = this._name
    }

    override writeValue(value: any): void {
        if (value != null) {
            var date = new Date(value)
            this._value = new MinsterDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
        }
    }

    onDateSelect() {
        let myDate = new Date(Date.UTC(this._value.year!, this._value.month! - 1, this._value.day!));

        var iso = myDate.toISOString();
        this._propagateChange(iso);
        this._showValidationError = false;
    }

    changeDays(day: any) {
        let dayValue = day.value === '' ? null : Number(day.value);
        this.validateAndUpdate(new MinsterDate(this._value.year, this._value.month, dayValue));

        if (this.isValidCellInput(day, 2, 3)) {
            this._two.nativeElement.focus();
            this._two.nativeElement.select();
        }
    }

    changeMonths(month: any) {
        let monthValue = month.value === '' ? null : Number(month.value);
        this.validateAndUpdate(new MinsterDate(this._value.year, monthValue, this._value.day));

        if (this.isValidCellInput(month, 2, 1) || Number(month) > 1) {
            this._three.nativeElement.focus();
            this._three.nativeElement.select();
        }
    }

    changeYears(year: any) {
        let yearValue = year.value === '' ? null : Number(year.value);
        this.validateAndUpdate(new MinsterDate(yearValue, this._value.month, this._value.day));

        if (this._showValidationError) {
            this.stateChanges.next();
        }
    }

    isValidCellInput(newValue: any, length: number, isGreaterThan: number): boolean {
        if (newValue && newValue) {
            const hasFullLength = newValue.value.length == length;
            const isValidShortCutForType = Number(newValue.value) > isGreaterThan;

            return hasFullLength || isValidShortCutForType;
        }

        return false;
    }

    private _isInvalidDate(): boolean {
        if (this.dateIsAllNull(this._value)) {
            return true;
        }

        const date = new Date(Date.UTC(this._value.year!, this._value.month! - 1, this._value.day!));

        return isNaN(date.valueOf()) || this.validate(date, this._value).hasError;
    }

    validateAndUpdate(newDate: MinsterDate) {
        this._value = newDate;

        if (this.dateIsAllNull(newDate)) {
            this._propagateChange('0001-01-01T00:00:00');
            this._showValidationError = true;
            this.setControlErrors({ invalidDate: true });
            
            return;
        }

        const date = new Date(Date.UTC(newDate.year!, newDate.month! - 1, newDate.day!));

        if (date instanceof Date && !isNaN(date.valueOf())) {
            const validationResult = this.validate(date, newDate);
            if (validationResult.hasError) {
                this.onDateError(validationResult.errorMessage);
                this.setControlErrors({ invalidDate: true });
            } 
            else {
                this.onDateSelect();
                this.setControlErrors(null); // Clear errors if valid
            }
        } 
        else {
            this.onDateError('Please enter a valid Date of Birth');
            this.setControlErrors({ invalidDate: true });
        }
    }

    private setControlErrors(errors: ValidationErrors | null): void {
        if (this.ngControl && this.ngControl.control) {
            this.ngControl.control.setErrors(errors);
        }
    }

    validate(date: Date, minsterDate: MinsterDate): DateValidation {
        if (this.isRolledOver(minsterDate, date)) {
            return new DateValidation(true, 'Please enter a valid Date of Birth');
        }

        if (this.isInThePast(date)) {
            return new DateValidation(true, 'Date must be in the past');
        }

        if (this.isTooOld(date)) {
            return new DateValidation(true, 'Must be less than 120 years old');

        }

        return new DateValidation(false, '');
    }

    isRolledOver(passedInDate: MinsterDate, createdDate: Date): boolean {
        let year = createdDate.getFullYear();
        let month = createdDate.getMonth() + 1;
        let day = createdDate.getDate();

        //Check value match as JS date rolls over values when to high e.g. day of 32 will become 1
        if (year == passedInDate.year && month == passedInDate.month && day == passedInDate.day) {
            return false;
        }

        return true;
    }

    isInThePast(inDate: Date): boolean {
        var today = moment();
        var dob = moment(inDate)
        return today.isBefore(dob);
    }

    isTooOld(inDate: Date): boolean {
        var today = moment();
        var dob = moment(inDate).add(120, 'years');

        return today.isAfter(dob);
    }

    dateIsAllNull(date: MinsterDate): boolean {
        if (date.year == null && date.month == null && date.day == null) {
            return true;
        }

        return false;
    }

    onDateError(errorMessage: string): void {
        this._propagateChange('error');
        this._labelData.validationErrorText = errorMessage;

        this._showValidationError = true;
        this.stateChanges.next();
    }

    allTouched(): boolean {
        if (this._inputs) {
            let inputs = this._inputs.toArray();

            let daysTouched = inputs[0].control.touched;
            let monthsTouched = inputs[1].control.touched;
            let yearsTouched = inputs[2].control.touched;

            return daysTouched && monthsTouched && yearsTouched;
        }

        return false;
    }


    onFocusIn() {
        if (!this.focused && this._disabled == false) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this._propagateTouched()
            this.focused = false;
            this.stateChanges.next();
        }
    }

    get empty() {
        return this._value.day == null && this._value.month == null && this._value?.year == null;
    }

    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    required = false;
    disabled = false;

    get errorState(): boolean {
        return this._showValidationError || this._isInvalidDate();
    }

    controlType = 'example-tel-input';

    @Input('aria-describedby') userAriaDescribedBy?: string;


    setDescribedByIds(ids: string[]): void {

    }

    onContainerClick(event: MouseEvent): void {
        if ((event.target as Element).tagName.toLowerCase() != 'input') {
            this._one.nativeElement.focus();
        }
    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
    }
}

class DateValidation {
    constructor(hasError: boolean, errorMessage: string) {
        this.hasError = hasError;
        this.errorMessage = errorMessage;
    }

    hasError: boolean;
    errorMessage: string;
}